/**
 * What one event row says, decided in a file with no imports.
 *
 * Every list of events in the app is `EventRow`, and every string on it comes
 * from here. The file is dependency-free for the reason `filmRank.ts`,
 * `exhibitionCard.ts` and `runPattern.ts` are: `entries.ts` reaches
 * `./calendar` without an extension, Node's test loader cannot resolve that,
 * and so anything importing it is untestable offline. These are the decisions
 * a screenshot would otherwise have to be read to check, so they belong where
 * `npm test` can reach them (CLAUDE.md: put a decision in a dependency-free
 * file and it can be tested from the Worker's suite).
 *
 * **Rule 2 of the brief, and B1/H2 of the decisions: no prose is composed
 * here.** Every string this file returns is a field of the row, a date
 * formatted, or one of `FIXED_LABELS`. There is no sentence, no rationale and
 * no generated line — `row-meta.test.ts` asserts it against a fixture.
 *
 * Its imports are `./places.ts`, `./zone.ts` and `./currency.ts`, by their full
 * names, each dependency-free for the same reason — so this file is still
 * loadable as it stands (cards plan WP4: the set's places live there because
 * the sheet reads them too; C2.1: the zones a row's times and "today" are read
 * in; C2.3: the symbol a row's price is printed with).
 */
import { aheadCount, placeIdOf, placesOf, setPlaceText, uniqueShowings, type Place } from './places.ts'
import { FALLBACK_ZONE, clockIn, dayIn, hereZone, zoneOfCity } from './zone.ts'
import { currencyOfCity, priceSymbol } from './currency.ts'

// A price's symbol is its row's region's (C2.3, D4); `priceSymbol` is
// re-exported so a caller that wants the symbol for a currency it already
// holds asks the file that prints prices.
export { priceSymbol }

/**
 * The row fields this file reads, declared rather than imported.
 *
 * `FeedRow` satisfies this structurally, so callers pass one straight in. The
 * alternative — `import type { FeedRow } from './types'` — would be erased by
 * Node's type stripping today and is one loader change away from not being,
 * and `types.ts` pulls the generated database types behind it.
 */
export interface RowEntry {
  id: string
  title: string
  type: string
  discipline?: string | null
  run_start?: string | null
  run_end?: string | null
  published_on?: string | null
  next_showing?: string | null
  next_opening?: string | null
  showing_count?: number | null
  venue?: string | null
  building?: string | null
  /** C2.1: the town, which names the region whose zone the row's times are
   *  read in (`zone.ts`'s `zoneOfCity`). Null is a Berlin row (trap T2). */
  city?: string | null
  /** The venue's own district. An exhibition's where line carries it where
   *  every other kind carries `runCount` — the brief's table. */
  district?: string | null
  organisation?: string | null
  /** Null on a row promoted from a newsletter that named a place no venue
   *  row matches (blind spots N3) — `newsletter.place_src` then holds the
   *  place as the mail wrote it. */
  venue_id?: string | null
  /** The venue's building where the venue is one of its halls. */
  building_id?: string | null
  /** Set only on a promoted newsletter lead, and absent on any row cached
   *  before N3 wrote one — read as `newsletter?.place_src ?? null`. */
  newsletter?: { place_src?: string | null } | null
  /** Cards WP3/WP4: every venue the card's set plays, by next showing.
   *  Absent on a row cached before WP3 — read through `placesOf`, never
   *  bare (the E5 trap). */
  places?: readonly Place[] | null
  is_free?: boolean | null
  price_min?: number | null
  price_max?: number | null
  price_note?: string | null
  english_access?: string
  /** The listing's own language tag, `en`/`de`/`en-GB`. Null on almost
   *  everything; read only where `english_access` says nothing (WP 6.1's
   *  talk line). */
  event_lang?: string | null
  /** Minutes, from the view's own column — 589 rows against `classical`'s 440
   *  and `stage`'s 19, which is why it is read first. */
  duration_min?: number | null
  film?: {
    rt_score?: number | null; metascore?: number | null; imdb_rating?: number | null
    /** WP 5.1's `whatLine`. `FilmFacts` writes all four. */
    directors?: string[]; year?: number; runtime_min?: number
    /** WP 6.1: the first one is the kind line's genre. */
    genres?: string[]
    /** Phase 6c, filled nightly by `src/reviews-run.ts`. A headline and a
     *  source, never a summary — the deciding line where a film has no
     *  score. */
    reviews?: { source: string; title: string }[]
  } | null
  classical?: {
    programme?: ProgrammeRef[]
    /** WP 5.1. `ClassicalFacts` writes all three; `soloists` carries an
     *  instrument this file does not print (see `decideLine`). */
    conductor?: string | null
    soloists?: { name: string }[]
    ensemble?: { name: string } | null
    duration_min?: number | null
  } | null
  /** Phase 5b's `StageFacts`, the fields a row says out loud. */
  stage?: {
    author?: string | null
    composer?: string | null
    company?: string | null
    director?: string | null
    conductor?: string | null
    is_premiere?: boolean
    duration_min?: number | null
    /** Phase 5b, same shape as a film's. */
    reviews?: { source: string; title: string }[]
  } | null
  /** Phase 7a's `ExhibitionFacts`. `text_src` is the gallery's own text, in
   *  English where the gallery published English — 235 of 335 rows — and the
   *  exhibition's deciding line quotes it. */
  exhibition?: { artists?: string[]; curators?: string[]; text_src?: string | null } | null
  /** What the adapters read off the house's own page, before the normalisers
   *  gave each kind its own column. Still the only source on a row whose
   *  recipe has not re-harvested since — which is why `whatLine` falls back to
   *  it in every branch rather than in one. */
  performers?: Record<string, string> | null
}

/** One programme item, structurally — `ProgrammeItem` satisfies it. Named
 *  rather than inline because `whatLine` reads it too, and two copies of the
 *  same literal is two places for a field name to be wrong. */
export interface ProgrammeRef {
  work?: string | null
  work_src?: string | null
  is_main?: boolean
  composer?: string | null
  composer_src?: string | null
}

/** One showing, structurally — `ShowingLite` and `ShowingRow` both satisfy it. */
export interface Showing {
  starts_at: string
  /** The building, so a film playing two halls of one multiplex counts once. */
  building_id?: string | null
  venue_id?: string | null
  venue?: string | null
  kind?: string | null
  /** OV/OmU/OmeU/DF as the house printed it on this showing (`versionOf` in
   *  `src/harvest/film.ts`). The row has no version of its own — subtitles are
   *  a fact about a screening, not about a film — so the film's what line
   *  reads them from here. */
  note?: string | null
}

/**
 * Every label this file can put on a row that is not a field of the row or a
 * formatted date.
 *
 * Exported because the test reads it: rule 2 is checked by asserting that
 * every word `metaLine` produced is either in a fixture field, in a date it
 * formatted, or in this set. A new label has to be added here to pass, which
 * is the point — it makes composing a sentence a visible act.
 */
export const FIXED_LABELS = [
  'Permanent', 'Last day', 'Until', 'Ended', 'From', 'Today', 'Tomorrow', 'No dates given',
  'Free', 'from', 'Subtitles', 'Some English', 'RT', 'Metacritic', 'IMDb',
  'cinema', 'cinemas',
  // Cards WP4: the count a where line prints for a set of three or more
  // places that are not cinemas (`setPlace`).
  'venues',
  // WP 5.1's `whatLine`: the one fixed word a stage row carries, and the unit
  // a runtime is printed in.
  'Premiere', 'min',
  'Concert', 'Opera', 'Dance', 'Theatre', 'Exhibition', 'Opening', 'Film', 'Talk', 'Market', 'Club',
  // Blind spots A2: a concert that is not classical — `kindWord`'s own split.
  'Gig',
  // 30 Sept: a festival, fair or garden day is not a talk. `kindWord`'s
  // fallback had been calling Gauklerfest and Erntedankfest "Talk".
  'Festival',
  // Nightlife N6 (D6): a pub quiz is its own type and its own word.
  'Quiz',
  // WP 6.1. `runCount`'s five shapes — "once", "last of 3", "51 showings to
  // Wed", "4 dates to 9 Oct", "opens 5 Oct", "3 weeks left" — and the two
  // role words a stage row's deciding line prints after a name.
  'once', 'last', 'of', 'to', 'opens', 'day', 'days', 'week', 'weeks', 'left',
  'showings', 'dates', 'dir.', 'cond.',
  // The hour in "1 h 45", and the word that says the composers named are the
  // ones the title did not already name.
  'h', 'also',
  // The language a row can carry, as words rather than as `english_access`'s
  // enum. `entries.ts`'s `EN_LABEL` says the same three; restated here for
  // the no-imports rule, and `Subtitles` above is `factOf`'s older spelling
  // of the middle one, kept because `metaLine` still returns it.
  'In English', 'English surtitles',
  // A screening's printed version, from the showing's own note.
  'OV', 'OmU', 'OmeU', 'DF',
] as const

// ---------------------------------------------------------------------------
// Dates
//
// `calendar.ts`'s `fmt` and `iso`, reproduced because this file may not import
// them. Same rules, deliberately: a date-only string is pinned to noon UTC and
// read back in UTC, so a `YYYY-MM-DD` never shifts a day for any reader; a
// row's timestamp is read in its own zone's wall clock (C2.1, `zone.ts`),
// because that is the time printed on the ticket, and "today" in the current
// region's.
// ---------------------------------------------------------------------------

const D = (s: string) => new Date(`${s}T12:00:00Z`)

// `toLocale*String` with an options bag builds a fresh `Intl.DateTimeFormat`
// on every call, and a feed of a thousand rows asks these questions several
// times per row per render: a profile put the three below at the top of every
// interaction's self time. The answers are pure functions of their arguments,
// so each is memoised in a bounded Map, cleared past `CACHE_CAP` entries.
const CACHE_CAP = 20_000
const memoised = <K extends string | number, V>(cache: Map<K, V>, key: K, make: () => V): V => {
  const hit = cache.get(key)
  if (hit !== undefined) return hit
  const out = make()
  if (cache.size >= CACHE_CAP) cache.clear()
  cache.set(key, out)
  return out
}

// …and a miss still paid for the formatter. Every render asks
// `berlinDay(now)` with a fresh `Date`, so the key is new every time: each of
// those built a formatter, and they filled the cap and cleared everything else
// out of it. One formatter per options bag, built once, makes a miss cheap
// (a4-13: this arrow was the top self-time entry of every interaction).
const formatters = new Map<string, Intl.DateTimeFormat>()
const formatter = (key: string, locale: string, o: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  let f = formatters.get(key)
  if (!f) { f = new Intl.DateTimeFormat(locale, o); formatters.set(key, f) }
  return f
}

// Every caller names a date field, so `toLocaleDateString` added no defaults
// and the formatter's output is the same string.
const fmtDayCache = new Map<string, string>()
const fmtDay = (s: string, o: Intl.DateTimeFormatOptions) => {
  const opts = JSON.stringify(o)
  return memoised(fmtDayCache, `${s}|${opts}`, () =>
    formatter(`day|${opts}`, 'en-GB', { timeZone: 'UTC', ...o }).format(D(s)))
}

/** The calendar day a moment falls on where the person is looking — the
 *  current region's (`zone.ts`'s `hereZone()`, Berlin's until C2.2; the name
 *  is older than regions). Not `slice(0, 10)`, which is the UTC day. An
 *  invalid date keeps `toLocaleDateString`'s 'Invalid Date'. A row's own
 *  timestamp is read in the row's zone instead (`rowDay`). */
export const berlinDay = (at: Date | string): string => dayIn(at, hereZone())

/** The day a row's own timestamp falls on, in its town's region's zone. */
const rowDay = (e: RowEntry, at: string): string => dayIn(at, zoneOfCity(e.city))

/**
 * The wall clock in `tz`, or null for the midnight a dateless extraction
 * wrote.
 *
 * The same rule `entries.ts`'s `localTime` applies, and for the same reason: a
 * row whose source gave a date and no time is stored at 00:00 — local
 * midnight in the row's own zone (D3) — and printing "00:00" would state a
 * midnight nobody wrote. `zone.ts` memoises per zone.
 */
function clockInOrNull(at: string | null | undefined, tz: string): string | null {
  if (!at) return null
  const t = clockIn(at, tz)
  return t === '00:00' || t === '24:00' ? null : t
}

/** A row's own clock for one of its timestamps, in the row's zone. */
const rowClock = (e: RowEntry, at: string | null | undefined): string | null =>
  clockInOrNull(at, zoneOfCity(e.city))

/**
 * Berlin's wall clock by name, or null for a bare midnight — **for the
 * Worker**, which imports it (`src/butler/tools/detail.ts`): the butler reads
 * Berlin until C1.4 gives it the person's region. Nothing in the app calls
 * it; the app reads a row's clock in the row's zone (`rowClock`).
 */
export function clockOf(at: string | null | undefined): string | null {
  return clockInOrNull(at, FALLBACK_ZONE)
}

/**
 * Which timestamp a row may print a clock from, or null.
 *
 * `exhibitionCard.ts`'s `clockAt`, applied here rather than imported for the
 * no-imports rule above. `showing_count` counts performances, so an `opening`
 * counts 0 and its `next_opening` is the only hour it has; above 1 there is no
 * single time to state and naming the first would claim it was *the* one.
 */
function clockSource(e: RowEntry): string | null {
  // Cards WP4: a film's count is its future showings over the set
  // (`aheadCount`), not `showing_count`, which keeps a film's past weeks.
  const n = aheadCount(e)
  if (n === 1) return e.next_showing ?? null
  if (n === 0) return e.next_opening ?? e.next_showing ?? null
  return null
}

/** A date, with the year only when it is not the year we are standing in — a
 *  run that opened last October and closes this September otherwise reads as
 *  a row whose start is after its end. */
function dateText(isoDay: string, now: Date): string {
  const sameYear = Number(isoDay.slice(0, 4)) === now.getUTCFullYear()
  return fmtDay(isoDay, sameYear
    ? { day: 'numeric', month: 'short' }
    : { day: 'numeric', month: 'short', year: 'numeric' })
}

// ---------------------------------------------------------------------------
// The title
// ---------------------------------------------------------------------------

/** The composer's surname, for a classical headline. `classicalForm.ts`'s own
 *  `surname`, narrowed to what a title needs. */
const lastWord = (name: string) => name.trim().split(/\s+/).pop() ?? name

/**
 * The one title function. E2: the row and the sheet print the same string, so
 * tapping a row never opens something that looks like a different event.
 *
 * A concert's own `title` is often "Sonderkonzert", the house's series name,
 * or — WP 8.1 — the orchestra and the conductor ("Junge Deutsche
 * Philharmonie, John Storgårds"), none of which name the evening's music. So
 * the anchor work leads whenever the programme has one, composer first,
 * **regardless of what `e.title` says** — unless `e.title` already names that
 * work itself, in which case the source wrote it and the row has no business
 * improving on it.
 *
 * `conciseTitle` in `classicalForm.ts` is the same idea and cannot be reached
 * from here (it imports `entries`); the anchor rule below is the subset a row
 * needs, and the sheet calls *this* rather than either of the two, so there is
 * one answer on screen.
 */
export function rowTitle(e: RowEntry): string {
  const main = mainWork(e)
  if (!main) return e.title
  if (titleNames(e.title, main.work)) return e.title
  // Never a composer borrowed from another item, and never `null · Work`: a
  // work the reference and the source both failed to attribute to anyone is
  // printed alone.
  return main.composer ? `${lastWord(main.composer)} · ${main.work}` : main.work
}

/** A gig: a concert in one of these (`entries.ts`'s `GIG_DISCIPLINES`,
 *  restated for the no-imports rule). */
const GIG_DISCIPLINES = new Set(['jazz', 'rock_pop', 'electronic'])

/** A set list is not a work, so a gig keeps whatever `e.title` says even
 *  where a programme was harvested for it. */
const NO_ANCHOR_DISCIPLINE = GIG_DISCIPLINES

/**
 * The work the evening is decided on, and who wrote it.
 *
 * Returns the item with `is_main: true` from the programme, or null if no
 * item is marked. The Worker's normaliser decided, and a card that decides
 * again is the two-deciders bug described in `docs/piece-plan/README.md`.
 */
function mainWork(e: RowEntry): { work: string; composer: string | null } | null {
  if (e.type !== 'concert') return null
  if (e.discipline && NO_ANCHOR_DISCIPLINE.has(e.discipline)) return null
  const items = e.classical?.programme ?? []
  const main = items.find(i => i.is_main)
  if (!main) return null
  return { work: main.work ?? main.work_src ?? '', composer: main.composer ?? main.composer_src ?? null }
}

/** The composer's surname in front of the work, where the programme names
 *  one — "Mahler, Symphony No. 5" rather than a bare work. Exported for the
 *  sheet's performers line, which prints the same pair. */
export const workWithComposer = (work: string, composer: string | null | undefined): string =>
  composer ? `${lastWord(composer)}, ${work}` : work

// ---------------------------------------------------------------------------
// The leading slot
// ---------------------------------------------------------------------------

export type Leading =
  | { kind: 'thumb'; url: string }
  | { kind: 'poster'; url: string }
  | { kind: 'plate'; big: string; small: string; hue: string }

/**
 * Which of the three leading slots this row gets.
 *
 * **D7: a venue photograph never appears on a row.** A photograph of the
 * Philharmonie is scene-setting on a sheet, where there is room to label it;
 * at 56px in a list it is a picture of a building standing in for a picture of
 * a concert, sixty rows running, and the label does not fit. So a row with
 * only a venue hero gets the time plate, which states a fact.
 *
 * `pic` is resolved by the caller. `heroUrl` reads `import.meta.env` and lives
 * in `entries.ts` behind an extensionless import, so this file cannot call it
 * and stay testable — the brief's signature is `leadingOf(e)`, and this is the
 * smallest change that keeps its return type, which is what the decision
 * actually is. Called with no `pic` every row is a plate, which is the honest
 * answer for a caller that has no pictures.
 */
export function leadingOf(
  e: RowEntry,
  pic?: { url?: string | null; isVenue?: boolean; poster?: string | null; now?: Date },
): Leading {
  if (pic?.poster) return { kind: 'poster', url: pic.poster }
  if (pic?.url && !pic.isVenue) return { kind: 'thumb', url: pic.url }
  return plateOf(e, pic?.now ?? new Date())
}

/** The plate on its own, for a row that has a picture but wants something
 *  behind it while the picture loads or after it fails. */
export const plateFor = (e: RowEntry, now: Date) => plateOf(e, now)

/**
 * Whether the plate draws the clock for this row — the one fact `metaLine`'s
 * `when` must not then repeat (WP 5.8).
 *
 * `plateOf` calls this rather than re-deriving `isRun` and `clock` beside it,
 * and `metaLine` reads the same call rather than working it out a second way
 * from its own inputs. Two places deciding what the plate shows is the bug
 * `CLAUDE.md` lists nine times — a row would lose its date the moment the two
 * disagreed about whether the plate had already stated it, and neither reading
 * would be wrong on its own.
 *
 * True only for a single occurrence whose clock is known: a run's plate shows
 * a weekday over a date, never an hour, and there the meta line's `Until …` /
 * `From …` names the one date the plate does not (`plateDay` picked the
 * *other* end); a dateless row's plate has no clock to duplicate either.
 */
function plateShowsClock(e: RowEntry, now: Date): boolean {
  const day = plateDay(e, now)
  if (!day) return false
  const isRun = !!e.run_end && e.run_end !== e.run_start
  return !isRun && rowClock(e, clockSource(e)) !== null
}

/**
 * The time plate: the clock or the weekday, over the date.
 *
 * Two lines, and neither of them is ever empty — a plate that printed a label
 * with nothing after it would be worse than a shorter row. Where there is no
 * date at all the plate says so in the small line and puts a dash above it,
 * rather than shouting an absence in serif.
 */
function plateOf(e: RowEntry, now: Date): { kind: 'plate'; big: string; small: string; hue: string } {
  const hue = hueOf(e)
  const day = plateDay(e, now)
  if (!day) return { kind: 'plate', big: '—', small: '', hue }

  const showsClock = plateShowsClock(e, now)
  const clock = showsClock ? rowClock(e, clockSource(e)) : null
  // A run has two dates and the hour means nothing, so the weekday leads and
  // the small line is the day of the month — the same two facts a single
  // evening carries, in the same two places, which is what keeps every row in
  // one column whatever the event is. WP 10.6/10.7: the small line always
  // carries the month — a two-line box has room for weekday-and-day *or*
  // day-and-month, never all three, so when the clock takes the big line the
  // small line drops the weekday (the big line's time already says "today"
  // by context) rather than appending a third token to an already crowded
  // line, which is what silently dropped the month for every clocked row.
  const big = clock ?? fmtDay(day, { weekday: 'short' })
  const small = fmtDay(day, { day: 'numeric', month: 'short' })
  return { kind: 'plate', big, small, hue }
}

/**
 * Which date the plate prints: the one the meta line is about.
 *
 * Not `run_start`, which is what this did first and which put "Thu 24 Sept" in
 * the plate beside "Until 30 Sept" in the line under it — two dates, both
 * true, disagreeing about what the row is asking you to decide. A run in
 * progress is about its end; a run that has not opened is about its start;
 * anything else is its one day.
 */
function plateDay(e: RowEntry, now: Date): string | null {
  const today = berlinDay(now)
  if (e.run_start && e.run_end && e.run_end !== e.run_start && !isPermanent(e, now)) {
    return e.run_start > today ? e.run_start : e.run_end
  }
  return e.run_start ?? e.published_on ?? null
}

/** One hue per kind of thing, as a token reference — `tokens.css` owns the
 *  values and both themes of them. Concert shares the accent's value on
 *  purpose: claret is the house colour and concerts are the house's subject.
 *  A gig is a concert here (the word splits, the colour does not); a club
 *  night had borrowed opera's purple until it got `--d-club` (blind spots
 *  A2), which made a night at SO36 read as an evening at the Staatsoper. */
export function hueOf(e: RowEntry): string {
  // First, whatever its discipline: a quiz is a quiz (nightlife N6, D6).
  if (e.type === 'quiz') return 'var(--d-quiz)'
  if (e.type === 'screening' || e.type === 'release' || e.discipline === 'film') return 'var(--d-film)'
  if (e.type === 'exhibition' || e.type === 'opening') return 'var(--d-exhib)'
  if (e.type === 'market' || e.type === 'festival') return 'var(--d-market)'
  if (e.discipline === 'opera') return 'var(--d-opera)'
  if (e.type === 'performance' || e.discipline === 'theatre' || e.discipline === 'dance') return 'var(--d-theatre)'
  if (e.type === 'concert') return 'var(--d-concert)'
  if (e.type === 'club_night') return 'var(--d-club)'
  return 'var(--d-talk)'
}

/** The word for what kind of thing a row is. One of `FIXED_LABELS`, always.
 *
 * Follows `hueOf`'s branches exactly, one word per branch, so the word and
 * the dot can never disagree — two of `hueOf`'s branches (exhibition/opening,
 * and the performance/theatre/dance one) each split into two words, because
 * a colour can carry one fact where a word can carry two, and Doug asked for
 * the second fact ("I don't even know if they're movies or not").
 */
export function kindWord(e: RowEntry):
  'Concert' | 'Gig' | 'Opera' | 'Dance' | 'Theatre' | 'Exhibition' | 'Opening' | 'Film' | 'Talk' | 'Market' | 'Festival' | 'Club' | 'Quiz' {
  if (e.type === 'quiz') return 'Quiz'
  if (e.type === 'screening' || e.type === 'release' || e.discipline === 'film') return 'Film'
  if (e.type === 'exhibition') return 'Exhibition'
  if (e.type === 'opening') return 'Opening'
  if (e.type === 'market') return 'Market'
  // A festival shares the market hue (the same family: fairs, markets, garden
  // days — Doug's D6) and gets its own word, because "Talk" on a street fair
  // was the catch-all speaking (verify-6, 30 Sept).
  if (e.type === 'festival') return 'Festival'
  if (e.discipline === 'opera') return 'Opera'
  if (e.discipline === 'dance') return 'Dance'
  if (e.type === 'performance' || e.discipline === 'theatre') return 'Theatre'
  if (e.type === 'concert') return e.discipline && GIG_DISCIPLINES.has(e.discipline) ? 'Gig' : 'Concert'
  if (e.type === 'club_night') return 'Club'
  return 'Talk'
}

// ---------------------------------------------------------------------------
// What it is
//
// WP 5.1's new line, and the one Doug asked for: "it's not clear what any of
// this stuff is." A title names an evening; this names the people in it.
// ---------------------------------------------------------------------------

/**
 * **What it is**, in italic serif under the title — the prototype's `sub`, and
 * the second of WP 6.1's four lines.
 *
 * **Rule 1 of the brief holds here hardest.** Every part of this is a field of
 * the row, a number, or one of `FIXED_LABELS`. There is no sentence.
 *
 * Two levels of punctuation, and they mean different things: a middle dot
 * separates two *fields* ("Fassbinder · 1974"), a comma separates several
 * names inside one ("Beethoven, Glass, Mendelssohn" is one programme, not
 * three facts). A row with two composers joined by dots would read as two
 * more facts about the evening.
 *
 * What each kind says is the brief's table, and it is not what WP 5.1 had
 * here: the people moved down to `decideLine` and this line became the thing
 * you need before you can read them — the version and the runtime for a film,
 * the rest of the programme and how long it runs for a concert, the language
 * and the length for a stage production, whose work it is for an exhibition.
 *
 * Branches on `kindWord` rather than on `e.type` and `e.discipline` again.
 * Re-deriving the kind here is how the dot, the word and the line come to
 * disagree about what a row is — the bug this codebase keeps writing. The
 * switch is exhaustive on purpose: splitting a `kindWord` branch is then a
 * compile error here rather than a row that quietly says nothing.
 *
 * **Market and Club return null, and that is the brief's stop condition, not
 * an oversight.** The table covers film, concert, opera, theatre, exhibition,
 * talk and tour; a market and a club night are neither, they carry no facts
 * column of their own, and `performers` is null on 98 of 99 club nights and 2
 * of 3 markets (NEXT.md, 27 Sept). An invented line for them would be prose.
 */
export function whatLine(e: RowEntry, ctx?: { showings?: Showing[] }): string | null {
  switch (kindWord(e)) {
    case 'Film': {
      const f = e.film ?? {}
      const p = e.performers ?? {}
      return joinFields([
        // Co-directors are one field: "Dardenne, Dardenne · 2019". An empty
        // array falls back too, not only a missing one: `[]` is the enricher
        // having run and found nobody, and the adapter's own `performers`
        // reading is still a name — `??` alone would have stopped at the empty
        // array and said nothing for a row that knows the director.
        joinNames(f.directors?.length ? f.directors : [p.director]),
        f.year != null ? String(f.year) : null,
        f.runtime_min != null ? `${f.runtime_min} min` : null,
        versionOf(ctx?.showings),
      ])
    }

    case 'Exhibition':
    case 'Opening':
      // Whose work, then whose selection, then whose building. `artists` is
      // 87% filled; curators are the honest answer for a themed group show
      // that names none, and the organisation for a listing that names
      // neither.
      return upToThree(e.exhibition?.artists)
        ?? upToThree(e.exhibition?.curators)
        ?? e.organisation
        ?? null

    case 'Opera':
    case 'Dance':
    case 'Theatre':
      // Can I follow it, and how long am I there. Dance sits with the other
      // two because it is the same `performance` row with the same `stage`
      // column behind it — one line, not a third one invented for it.
      return joinFields([languageLabel(e), durationText(e)])

    case 'Concert': {
      // What else is on, once the title has named the piece it names. "also"
      // only where the title actually names one of them — otherwise the
      // programme is the whole answer and the word would be dangling.
      const composers = composerSurnames(e.classical?.programme)
      const title = rowTitle(e)
      const rest = composers.filter(n => !titleNames(title, n))
      const also = rest.length < composers.length && rest.length > 0
      const programme = upToThree(rest)
      return joinFields([programme ? (also ? `also ${programme}` : programme) : null, durationText(e)])
    }

    case 'Talk':
      // Six talks in the store and every one of them `unknown` (WP 6.6 is the
      // pass that changes that), so this is empty today and correct tomorrow.
      return languageLabel(e)

    case 'Quiz':
      // Can I play it: the one question a quiz's line answers (D1 recommends
      // by language and distance). "In English" from `english_access` or an
      // `event_lang` that starts with `en`; a German-only quiz says nothing
      // here, and the app's language filter is what hides it.
      return languageLabel(e)

    case 'Gig':
      // No programme to name — the title is the act — so only how long.
      return durationText(e)

    case 'Market':
    case 'Festival':
    case 'Club':
      return null
  }
}

/**
 * The language a row carries, as one of three fixed words, or null.
 *
 * `entries.ts`'s `EN_LABEL` and `enWarning`, restated for the no-imports rule
 * and with `enWarning`'s judgement kept: `unknown` is a third of the data and
 * saying "Unclear" on a third of the feed is noise, and `not_applicable` says
 * only that an exhibition is an exhibition.
 *
 * `native` *is* printed here, where `enWarning` drops it: the brief's talk and
 * tour line is exactly "In English", and on a screen that hides German-only
 * talks the reassurance is the fact.
 *
 * **Not the brief's "surtitles in English and German".** That phrase is in
 * `stage.surtitles_src` and in German — "Sprache: In italienischer Sprache mit
 * deutschen und englischen Übertiteln" — on 50 of 702 performances, up to 130
 * characters, and reducing it to which two languages needs a German parser,
 * which is WP 6.6's. `english_access` is the field that already answers the
 * question the phrase is asked for, on every row.
 */
function languageLabel(e: RowEntry): string | null {
  if (e.english_access === 'native') return 'In English'
  if (e.english_access === 'surtitles') return 'English surtitles'
  if (e.english_access === 'partial') return 'Some English'
  // Only where the enum said nothing: a listing that tagged itself `en` is
  // still a statement, and `english_access` is `unknown` on every talk today.
  if (/^en/i.test(e.event_lang ?? '')) return 'In English'
  return null
}

/**
 * How long it runs — "1 h 45", or "95 min" under the hour.
 *
 * The view's own `duration_min` first, because it is filled on 589 rows
 * against `classical`'s 440 and `stage`'s 19, and the two columns behind it
 * where a row predates it. An exact hour is "2 h", never "2 h 0".
 */
function durationText(e: RowEntry): string | null {
  const min = e.duration_min ?? e.classical?.duration_min ?? e.stage?.duration_min
  if (min == null || min <= 0) return null
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  const rest = min % 60
  return rest ? `${h} h ${String(rest).padStart(2, '0')}` : `${h} h`
}

/**
 * The printed version of a screening — OV, OmU, OmeU, DF — from the showings.
 *
 * A row has no version of its own and cannot: subtitles are a fact about one
 * screening, not about a film, and the same film plays OV at one cinema and
 * OmU at another (99 showings say OmU today, 10 say OV). Ordered rather than
 * first-seen, for `filmRank.ts`'s reason: a line reading "OmU, OV" one render
 * and "OV, OmU" the next is the same fact looking like a change.
 */
const VERSION_ORDER = ['OV', 'OmU', 'OmeU', 'DF'] as const

function versionOf(showings: Showing[] | undefined): string | null {
  if (!showings?.length) return null
  const seen = new Set<string>()
  for (const s of showings) {
    const note = s.note?.trim()
    if (note && (VERSION_ORDER as readonly string[]).includes(note)) seen.add(note)
  }
  const out = VERSION_ORDER.filter(v => seen.has(v))
  return out.length ? out.join(', ') : null
}

/**
 * Does `title` already contain `needle`? Matched at a word start with the end
 * left open, which is CLAUDE.md's rule for German: "Bach" must match "Bachs"
 * and must not match "Offenbach".
 *
 * Named for its first use — a composer's surname — but the match itself
 * knows nothing about surnames: `rowTitle` (WP 8.1) uses it to ask whether
 * `e.title` already names the anchor *work*, and `titleExtras` uses it in the
 * other direction, to ask whether a performer credit is already in `who`.
 */
function titleNames(title: string, needle: string): boolean {
  if (!needle) return false
  const esc = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`(^|[^\\p{L}])${esc}`, 'iu').test(title)
}

/**
 * A value the row can print, or null: trimmed of whitespace, and of a
 * separator the source left dangling at the end of it.
 *
 * `performers.soloists` on the DSO's own listings reads "Deutsches
 * Symphonie-Orchester Berlin, Orchestra, Gil Shaham," — the adapter kept the
 * comma that separated it from a field it did not read. A trailing separator
 * is the row printing punctuation with nothing after it, and on the card it
 * reads as a line that was cut when it was not.
 */
const field = (x: string | null | undefined): string | null =>
  x?.trim().replace(/[,;·]+$/, '').trim() || null

/** Two facts, with a middle dot between them. Empty parts drop out, so a film
 *  with no year is "Fassbinder · 93 min" rather than "Fassbinder ·  · 93 min". */
const joinFields = (parts: (string | null | undefined)[]): string | null =>
  parts.map(field).filter((x): x is string => !!x).join(' · ') || null

/** Several names that are one fact. See `whatLine`'s note on the two levels of
 *  punctuation. The instrument is dropped from a soloist here for the reason
 *  `whoLine` drops it: this joins with commas, so "piano" would read as
 *  another player. */
const joinNames = (parts: (string | null | undefined)[]): string | null =>
  parts.map(field).filter((x): x is string => !!x).join(', ') || null

/**
 * Up to three names, then "+N".
 *
 * `artistNames` in `exhibitionCard.ts`, the rule applied here rather than
 * imported — this file has no imports, for the reason its header gives. A
 * group show lists eleven names and a row is not a wall label.
 */
function upToThree(names: readonly string[] | undefined): string | null {
  const clean = (names ?? []).map(n => n.trim()).filter(Boolean)
  if (!clean.length) return null
  return clean.length <= 3
    ? clean.join(', ')
    : `${clean.slice(0, 3).join(', ')} +${clean.length - 3}`
}

/**
 * The programme's composers, by surname, deduplicated, in the order played.
 *
 * Deduplicated because an all-Bach evening is six programme items and one
 * composer, and "Bach, Bach, Bach" is the row counting pieces when it was
 * asked who wrote them. In order rather than sorted: the running order is the
 * one thing the source did decide.
 *
 * `composer_src` is the fallback for an item the reference never matched —
 * the printed name is still a name, and a row that dropped it would say
 * nothing about a work it can see.
 */
function composerSurnames(programme: ProgrammeRef[] | undefined): string[] {
  const out: string[] = []
  for (const item of programme ?? []) {
    const name = item.composer ?? item.composer_src
    if (!name) continue
    const surname = lastWord(name)
    // A printed programme line the normaliser could not read comes through as
    // punctuation — "***" is in the store today — and a row is not improved by
    // three asterisks where a composer should be.
    if (!/\p{L}/u.test(surname)) continue
    if (!out.includes(surname)) out.push(surname)
  }
  return out
}

// ---------------------------------------------------------------------------
// When
// ---------------------------------------------------------------------------

/** How long a run has to be before it stops being a date and becomes a fact
 *  about the building. F6's own two tests: over two years, or ending after the
 *  end of next year. */
const PERMANENT_DAYS = 730

/**
 * Is this a run nobody needs to hurry for?
 *
 * The collection displays say "until 2029" or nothing at all, and a countdown
 * on one is noise — worse, it is a countdown that never moves, which teaches
 * you to stop reading the when column.
 */
export function isPermanent(e: RowEntry, now: Date): boolean {
  if (!e.run_end) return false
  const end = D(e.run_end).getTime()
  const start = e.run_start ? D(e.run_start).getTime() : now.getTime()
  if (end - start > PERMANENT_DAYS * 86400000) return true
  return Number(e.run_end.slice(0, 4)) > now.getUTCFullYear() + 1
}

/**
 * The bold part of the meta line: the one thing about when.
 *
 * A single evening is its clock, because that is what you plan around. A run
 * is how much of it is left, because "is there still time" is the only
 * question a three-month show poses. Neither is a sentence.
 *
 * `showings` is read only where the row's own `next_showing` cannot answer —
 * a film week whose entry carries several performances has no single time to
 * state, and the showings are where the next one actually is.
 */
export function whenText(e: RowEntry, now: Date, showings?: Showing[]): string {
  const today = berlinDay(now)

  if (isPermanent(e, now)) return 'Permanent'

  const isRun = !!e.run_start && !!e.run_end && e.run_end !== e.run_start
  if (isRun) {
    if (e.run_end === today) return 'Last day'
    // A run that has closed. Not "Until", which would read as time remaining
    // on something that has none — one value meaning both the end date and
    // whether it is ahead is the bug this codebase keeps writing.
    if (e.run_end! < today) return `Ended ${dateText(e.run_end!, now)}`
    if (e.run_start! > today) return `From ${dateText(e.run_start!, now)}`
    return `Until ${dateText(e.run_end!, now)}`
  }

  const day = e.run_start ?? e.published_on
  if (!day) return 'No dates given'

  // A row carrying several performances has no single time of its own
  // (`clockSource` returns null above 1), and the next one still ahead is the
  // useful fact. Only the showings know it, which is why they are a parameter.
  const at = clockSource(e) ?? nextStart(showings, now)
  const onDay = at ? rowDay(e, at) : day
  const clock = rowClock(e, at)

  if (onDay === today) return clock ?? 'Today'
  // Every date carries its month — "Sat 24" was the line Doug photographed,
  // cut short of the one thing he needed. `dateText` already decides whether
  // the year belongs (same year: none; otherwise: yes), so the weekday is
  // added to its answer rather than a second copy of that rule living here.
  const dayText = onDay === tomorrowOf(today)
    ? 'Tomorrow'
    : `${fmtDay(onDay, { weekday: 'short' })} ${dateText(onDay, now)}`
  return clock ? `${dayText} · ${clock}` : dayText
}

const tomorrowOf = (isoDay: string) => {
  const d = D(isoDay)
  return new Date(d.getTime() + 86400000).toISOString().slice(0, 10)
}

/** The next showing still ahead, ignoring run markers — they are annotations
 *  about the span, not occurrences. */
function nextStart(showings: Showing[] | undefined, now: Date): string | null {
  if (!showings?.length) return null
  const from = now.toISOString()
  let best: string | null = null
  for (const s of showings) {
    if (s.kind === 'run') continue
    if (s.starts_at < from) continue
    if (best === null || s.starts_at < best) best = s.starts_at
  }
  return best
}

// ---------------------------------------------------------------------------
// The one fact
// ---------------------------------------------------------------------------

/**
 * The single fact worth the end of the meta line, or null.
 *
 * Ranked, because a row has room for one: price first (it is the only one that
 * can stop you going), then the language doubt, then a rating. Returns null
 * rather than a placeholder — a row with nothing to add should say nothing,
 * and 69% of rows have no price at all.
 *
 * The cinema count is not here and cannot be: nothing on the row counts
 * distinct buildings, only the showings do. `metaLine` adds it as the
 * fallback, where it has them.
 */
export function factOf(e: RowEntry): string | null {
  if (e.is_free) return 'Free'
  if (!placeholderPrice(e)) {
    if (e.price_min != null && e.price_max != null && e.price_min !== e.price_max) {
      return `from ${priceSymbol(currencyOfCity(e.city))}${e.price_min}`
    }
    if (e.price_min != null) return `${priceSymbol(currencyOfCity(e.city))}${e.price_min}`
  }

  // Only the two verdicts that are a fact about the evening. `unknown` is a
  // third of the data and "Unclear" as a row's one fact is noise, `native`
  // and `not_applicable` are good news and need no flag, and `none` gets no
  // tag here: for an `english_only` profile it never reaches a row
  // (`excludedAccess`), and for one who turned that off it is not news.
  if (e.english_access === 'surtitles') return 'Subtitles'
  if (e.english_access === 'partial') return 'Some English'

  const f = e.film ?? {}
  if (typeof f.rt_score === 'number') return `RT ${f.rt_score}%`
  if (typeof f.metascore === 'number') return `Metacritic ${f.metascore}`
  if (typeof f.imdb_rating === 'number') return `IMDb ${f.imdb_rating.toFixed(1)}`

  return null
}

/**
 * A price of nothing on a row that is not free — a source that published a
 * placeholder, not an evening you can walk into.
 *
 * 219 rows carry `lowPrice: 0.00, highPrice: 0.00` in their schema.org markup
 * with `is_free` false, the Philharmonie's own concerts among them, and "€0"
 * on a €68 concert is the row stating a fact it does not have. `is_free` is
 * the field that says free — 28 rows do, and six of those also say 0.
 *
 * The bug this codebase keeps writing, in the price column: `price_min = 0`
 * was doing two jobs, "this costs nothing" and "nobody told us", and the two
 * disagree on 219 rows out of 225. Read by `factOf` and by `priceText`, so
 * the one-line row and the card cannot answer it differently.
 */
export function placeholderPrice(e: RowEntry): boolean {
  return !e.is_free && e.price_min === 0 && (e.price_max == null || e.price_max === 0)
}

/**
 * The sheet's Price fact, or null where nobody published one.
 *
 * UI-audit a1-31: this used to answer "Not given" on every row with no price,
 * because Doug once asked to see the question answered rather than left out.
 * On 2 Oct he chose the other way — "show only facts that are known" — after
 * a Grips sheet where three of four facts said Not given / Unclear. So null
 * is "not known" and the sheet leaves the row out; there is no sentinel
 * string for a caller to compare against.
 *
 * UI-audit a1-10: the sheet had its own copy of this in `entries.ts` that
 * never learned `placeholderPrice`, so the row printed nothing and the
 * sheet printed "€0" for the same Philharmonie concert. A placeholder zero
 * is "Not given", and so is its `price_note` — on all 224 such rows that is
 * the raw schema.org fragment the zero came from, not the house's words.
 */
export function priceFact(e: RowEntry): string | null {
  if (e.is_free) return 'Free'
  if (placeholderPrice(e)) return null
  const { price_min: lo, price_max: hi } = e
  const sym = priceSymbol(currencyOfCity(e.city))
  if (lo != null && hi != null && lo !== hi) return `${sym}${lo}–${hi}`
  if (lo != null) return `${sym}${lo}`
  return e.price_note || null
}

/** How many separate buildings this row plays in. Buildings, not halls: a
 *  multiplex with three screens is one cinema to someone deciding where to
 *  go. */
export function cinemaCount(showings: Showing[] | undefined): number {
  if (!showings?.length) return 0
  const seen = new Set<string>()
  for (const s of showings) {
    if (s.kind === 'run') continue
    const key = s.building_id ?? s.venue_id ?? s.venue
    if (key) seen.add(key)
  }
  return seen.size
}

// ---------------------------------------------------------------------------
// The meta line
// ---------------------------------------------------------------------------

/**
 * The place a row prints: the building (or the hall, on a place page), and
 * where no venue row exists, the place a newsletter named — as text, in the
 * venue's own slot and style, with nothing to link to (blind spots K2: a
 * newsletter event is an event, venue or no venue). The organisation after
 * that, as before. `?.` and `?? null` because a row cached before N3 has no
 * `newsletter` field at all (the E5 trap).
 */
function placeName(e: RowEntry, showHall?: boolean): string {
  const hall = showHall ? e.venue : e.building ?? e.venue
  const mailed = e.venue_id == null ? e.newsletter?.place_src ?? null : null
  return hall ?? mailed ?? e.organisation ?? ''
}

/**
 * Cards WP4: the place a card prints when the card is a set — `Zoo Palast,
 * Rollberg Kinos`, or `10 cinemas` — and null when it is one building or the
 * row carries no `places` (cached before WP3). Null is the instruction to
 * print `placeName` exactly as before, which is why a one-place card does
 * not change.
 *
 * The canonical's own `venue` is one member's — The Odyssey's was cinematic's
 * b-ware row while the film played ten cinemas, and the card said "b-ware!
 * ladenkino +9". The set is what the card is (the plan's principle: a card is
 * the set, never a member), so the set is what it names.
 *
 * Not on a place page (`showHall`): there every row is listed under the
 * building it plays, and "10 cinemas" under Zoo Palast's heading would be the
 * row arguing with the page it is on.
 */
export function setPlace(e: RowEntry, ctx?: { showHall?: boolean }): string | null {
  if (ctx?.showHall) return null
  return setPlaceText(placesOf(e), kindWord(e) === 'Film' ? 'cinemas' : 'venues')
}

/**
 * The row's last line, in five parts the component draws in one line with one
 * ellipsis: a discipline dot, the kind, when, where, and one fact.
 *
 * `dot` is a colour token rather than text. It used to be the *only* thing on
 * a row saying what kind of evening this is, which is what Doug's "it's not
 * clear what any of this stuff is" was about — WP 5.0 added the word and WP
 * 5.1 puts it here, in front of the dot's own colour, so the two say the same
 * thing in two registers rather than one of them saying it alone. `kind` is
 * `kindWord`'s, never re-derived: one function decides, the dot follows
 * `hueOf`, and the two cannot drift.
 *
 * The venue is the building, not the hall: "Philharmonie Berlin, Grosser Saal"
 * is two facts and the second is only worth room on a place page, where every
 * row shares the first — which is what `showHall` is.
 *
 * **WP 5.8: `when` is empty where the plate already shows the clock.** At
 * 390px the line has room for one date, and the plate two centimetres to the
 * left already drew it — `Concert · Until 27 Sept · K…` was the venue paying
 * for a date said twice. `plateShowsClock` is the one function that decides
 * this, read here rather than re-derived: a run keeps `Until …` (the plate
 * never states the end), and a dateless row keeps whatever `whenText` gives
 * it (the plate has nothing to duplicate).
 *
 * That is conditional on a plate actually being there. `EventRow`'s poster
 * kind fills the whole 64×96 slot itself — `Leader` draws no plate behind it
 * (`plate = kind.kind === 'poster' ? null : plateFor(...)`) — and Picks'
 * cards, the sheet, and the lists this package does not touch never call
 * `leadingOf` at all, so nothing there duplicates the date either. `ctx.leading`
 * is `leadingOf`'s own result — the caller's decision, not a second guess at
 * it — and is `undefined` for every one of those; only a caller that passes
 * a `'plate'` or `'thumb'` kind (never a `'poster'`, and only ever a kind
 * `leadingOf` itself returned) gets the date dropped.
 */
export function metaLine(
  e: RowEntry,
  ctx: { now: Date; showings?: Showing[]; showHall?: boolean; leading?: Leading['kind'] },
): { dot: string; kind: string; when: string; venue: string; fact: string | null } {
  const cinemas = cinemaCount(ctx.showings)
  const showsPlate = ctx.leading !== undefined && ctx.leading !== 'poster'
  // Cards WP4: a set names itself in the venue slot, and then the count of
  // cinemas would be the same fact twice.
  const set = setPlace(e, ctx)
  return {
    dot: hueOf(e),
    kind: kindWord(e),
    when: showsPlate && plateShowsClock(e, ctx.now) ? '' : whenText(e, ctx.now, ctx.showings),
    venue: set ?? placeName(e, ctx.showHall),
    fact: factOf(e) ?? (!set && cinemas > 1 ? `${cinemas} cinemas` : null),
  }
}

// ---------------------------------------------------------------------------
// WP 6.1: the four lines of the card
//
// The prototype's `kindLine`, `decideText`, `whereText` and `runCount`
// (`docs/proto-plan/prototype/app.html`), ported. Doug approved that card on
// real rows after three rounds, and 6.2, 6.3 and 6.4 render it unchanged — so
// these four signatures are an interface three packages build against, and
// the anatomy is settled:
//
//   [picture 60x88]  kind · genre or form · WHEN                      (heart)
//                    Title in serif, two lines max
//                    what it is, italic serif, one line
//                    the deciding line, up to two lines
//                    WHERE in bold · once / N showings / weeks left · price
//
// `metaLine` above is **not** replaced: `Checklist`, `PurchaseList`,
// `CollectionList`, `TaskList`, `Hidden` and `EventSheet` all draw a one-line
// row from it, none of them is this package's file, and a row that says one
// thing on Browse and another on a task list is the disagreement this file
// exists to prevent. `EventRow` is the only caller that moved.
// ---------------------------------------------------------------------------

/** One run of text in a line the component draws, and whether it is the bold
 *  one. Returned rather than an HTML string: the prototype could interpolate
 *  `<b>`, a React component cannot, and a string with markup in it is a string
 *  no test can read a word out of. */
export interface LinePart {
  text: string
  /** Drawn in `--ink` at 600 — one per line at most. */
  strong?: boolean
}

/**
 * **The kind line**: the dot, the word, the genre or form, and when.
 *
 * `kind` is `kindWord`'s and `dot` is `hueOf`'s, never re-derived — one
 * function decides what a row is and the colour follows the word.
 *
 * `genre` is a film's `film.genres[0]` and nothing else of its own. A
 * concert's *form* — Orchestral, Chamber, Recital — is `classicalForm.ts`'s,
 * which reads `room_scale` and a page of German regexes and imports
 * `./types`; this file has no imports, for the reason its header gives, so the
 * caller resolves it and passes it in as `ctx.form`. That is `leadingOf`'s own
 * arrangement with `heroUrl`, and for the same reason: copying sixty regexes
 * here would be a second source of truth for what a concert is, and the two
 * would drift on the first recipe that changed.
 *
 * **`when` was `whenText`, always, from WP 6.1 to 6.7** — where WP 5.8 had
 * dropped it whenever the plate already showed the clock. That rule existed
 * because the kind, the date, the venue and one fact shared a single 390px
 * line and the venue paid for the duplication; on this card the venue has a
 * line of its own and nothing truncates it (rule 3), which is why 6.1 read
 * the reason as gone. WP 6.9 found it was not: on a *plated* single-evening
 * row the plate still states the same clock four lines above this one
 * (`20:00` / `Sun 27`, then `Concert · Orchestral · 20:00`), and a viewer
 * scanning the card reads it twice before reading anything else.
 *
 * **`ctx.plateVisible` drops `when`'s clock, and only for the row that
 * passes it.** WP 5.8's mistake was scoping the suppression to the *screen*
 * — every row on the one-line list, whether or not that row actually drew a
 * plate — which is why removing it broke nothing and restoring it the same
 * way would only reintroduce the screen-wide coupling this file's header
 * warns about. `EventRow` is the only caller that ever draws a plate.
 *
 * **Named for what the reader can see, not for what `leadingOf` returned —
 * a first draft of this flag conflated the two and got it backwards.**
 * `leadingOf` returns `'plate'` (no picture: the plate is the whole leading
 * slot) and `'thumb'` (a picture: `Leader`'s own `.ev-lead > img` is
 * `position: absolute; inset: 0`, painted *over* the plate it shares the slot
 * with — `EventRow.css`'s own comment on that rule says "a thumbnail
 * *covers* it"). Passing `leading.kind !== 'poster'` — true for both — read
 * as "a plate element exists," which is not this line's question; the
 * question is whether its clock is the thing a reader actually sees, which
 * is true for `'plate'` alone. Wrong in the direction that hides real
 * information: a house's own photograph is the common case for a concert
 * (WP 6.1 measured 15 rows drawing a bare plate against 89 drawing a
 * picture), and every one of those rows drew a plate with a clock sealed
 * under the photograph and a kind line with nothing in it either — the
 * evening's own time, printed nowhere on the row.
 *
 * A run's plate never states an hour (`plateShowsClock` says so) and a
 * dateless row's plate has nothing to duplicate either, so `when` still comes
 * back non-empty on both regardless of `plateVisible` — this flag says the
 * plate is what the reader sees, not that it says an hour.
 *
 * **Absent (`undefined`) is the same as `false`: the clock is never
 * dropped.** That is every caller before this package — `Picks.tsx` and
 * `EventSheet.tsx` never draw a plate at all and pass nothing; `FeedCard.tsx`
 * calls `leadingOf` for its hero but never renders `.ev-plate`'s clock
 * digits over it, so it passes nothing either. A caller that started
 * drawing a plate later and forgot to pass `plateVisible` gets the *old*,
 * safer answer (the clock shown, possibly twice) rather than the *wrong* one
 * (the clock silently missing) — the one direction this flag may fail in.
 *
 * One window this still cannot see into: `Leader` catches a failed image
 * load and falls back to a bare plate *after* first paint, inside its own
 * `failed` state — a fact this file has no way to read, since `kindLine` is
 * computed once, above `Leader`, off the `leading` its caller already
 * decided. A thumb row whose picture 404s keeps its kind line's own clock
 * printed (the safe direction) and, once the fallback plate mounts, prints
 * it a second time on the plate too — the exact redundancy this flag exists
 * to remove, for as long as that picture keeps failing. Narrow (a broken
 * image, not the ordinary case) and left as recorded here rather than wired
 * up: reaching it would mean lifting `Leader`'s local `failed` state to the
 * caller that computes `kindLine`, coupling two components over one rare
 * edge rather than the row's usual shape.
 */
export function kindLine(
  e: RowEntry,
  ctx: { now: Date; showings?: Showing[]; form?: string | null; plateVisible?: boolean },
): { dot: string; kind: string; genre: string | null; when: string } {
  const kind = kindWord(e)
  const genre = kind === 'Film'
    ? (e.film?.genres?.[0] ?? null)
    : (e.type === 'concert' ? ctx.form ?? null : null)
  const word = genre?.trim() || null
  return {
    dot: hueOf(e),
    kind,
    // Dropped where it only repeats the word beside it: one `concert` row in
    // the store carries `discipline = 'opera'`, which `kindWord` calls Opera
    // and `classicalForm` calls opera, and "Opera · Opera" is the row
    // spending a separator on nothing.
    genre: word && word !== kind ? word : null,
    when: ctx.plateVisible && plateShowsClock(e, ctx.now) ? '' : whenText(e, ctx.now, ctx.showings),
  }
}

/**
 * Who the row's own title named, where `decideLine`'s own reading of
 * `classical`/`performers` said nothing at all — WP 8.1's "who plays moves
 * down." `rowTitle` now overwrites a concert's title with its anchor work
 * whenever one is found, so a title like "Junge Deutsche Philharmonie, John
 * Storgårds" would otherwise vanish rather than move.
 *
 * Only reached where `rowTitle` actually replaced the title: a row whose
 * title still stands has nothing to recover, because the title is still on
 * the card. And only where `who` is empty: `conductor`/`soloists`/`ensemble`
 * are a house's own structured credits, and a title that only repeats what
 * they already say would be the row stating a fact twice. Returned whole,
 * never split apart and rejoined — a row printing the title's own words is
 * not composing a sentence about them.
 */
function titleExtras(e: RowEntry, who: string | null): string | null {
  if (who) return null
  if (rowTitle(e) === e.title) return null
  return e.title
}

/**
 * **The deciding line**: the one thing that decides it, per kind, or nothing.
 *
 * The prototype's `decideText`. What each kind puts here is the brief's table
 * and it is the package's whole point — a film is decided on its scores, a
 * concert on who is playing, a production on who made it, an exhibition on
 * what the gallery says it is about.
 *
 * Up to two lines on screen (rule 3), which is why an exhibition gets 120
 * characters and a film's headline is not cut.
 *
 * Returns parts rather than a string so the component can bold the first
 * score without this file writing markup. An empty array is a row with
 * nothing to add, and the line is then absent rather than blank.
 *
 * Market and Club return nothing, for the reason `whatLine` does.
 */
export function decideLine(e: RowEntry): LinePart[] {
  const p = e.performers ?? {}

  switch (kindWord(e)) {
    case 'Film': {
      const f = e.film ?? {}
      const scores: LinePart[] = []
      if (typeof f.rt_score === 'number') scores.push({ text: `RT ${f.rt_score}%`, strong: true })
      if (typeof f.metascore === 'number') scores.push({ text: `Metacritic ${f.metascore}` })
      if (typeof f.imdb_rating === 'number') scores.push({ text: `IMDb ${f.imdb_rating.toFixed(1)}` })
      if (scores.length) {
        // The first one carries the weight whichever it is: a film with no RT
        // score and a Metascore should still have one thing you read first.
        scores[0].strong = true
        return scores
      }
      // A headline and where it ran, never a summary — `src/reviews.ts` writes
      // only the two. 59 films carry one and 220 carry a score, so this is the
      // second answer and not the usual one.
      return quotedReview(f.reviews)
    }

    case 'Concert':
    case 'Gig': {
      const c = e.classical
      const who = c
        ? joinFields([c.conductor, joinNames((c.soloists ?? []).map(s => s.name)), c.ensemble?.name])
        : joinFields([p.conductor, p.soloists, p.ensemble])
      const combined = joinFields([who, titleExtras(e, who)])
      return combined ? [{ text: combined }] : []
    }

    case 'Opera':
    case 'Dance':
    case 'Theatre': {
      const st = e.stage
      const director = st?.director ?? p.director
      const conductor = st?.conductor ?? p.conductor
      const made = joinFields([
        // The role after the name, abbreviated: "Barrie Kosky dir." is the
        // programme's own shorthand and costs four characters where "directed
        // by" would cost twelve and read as a sentence.
        director ? `${director.trim()} dir.` : null,
        conductor ? `${conductor.trim()} cond.` : null,
        st?.is_premiere ? 'Premiere' : null,
      ])
      return [...(made ? [{ text: made }] : []), ...quotedReview(st?.reviews)]
    }

    case 'Exhibition':
    case 'Opening': {
      const text = cutAtWord(e.exhibition?.text_src, 120)
      return text ? [{ text }] : []
    }

    case 'Talk': {
      // Whoever the adapter read. A talk has no facts column of its own, so
      // `performers` is all there is, and its values are already fields.
      const who = joinFields(Object.values(p)) ?? e.organisation ?? null
      return who ? [{ text: who }] : []
    }

    case 'Market':
    case 'Festival':
    case 'Club':
    case 'Quiz':
      return []
  }
}

/** A headline with its source in front of it, quoted. The quotation marks are
 *  the claim that these are somebody else's words and not the app's — the one
 *  place rule 1 lets a row carry a sentence, because it is attributed. */
function quotedReview(reviews: { source: string; title: string }[] | undefined): LinePart[] {
  const r = reviews?.[0]
  if (!r?.title?.trim()) return []
  return [{ text: `${r.source}: “${r.title.trim()}”` }]
}

/**
 * Cut to a length at a word boundary, with an ellipsis, or the whole thing.
 *
 * Rule 1 of the brief: the gallery's text is quoted with no edit but this cut.
 * At a word boundary because cutting mid-word reads as a fault rather than as
 * a continuation, and `text_src` runs to 1,300 characters on the long ones.
 */
function cutAtWord(text: string | null | undefined, max: number): string | null {
  const clean = (text ?? '').replace(/\s+/g, ' ').trim()
  if (!clean) return null
  if (clean.length <= max) return clean
  const cut = clean.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:.–—-]+$/, '')}…`
}

/**
 * **The where line**: the venue in bold, then how often, then the price.
 *
 * The prototype's `whereText`. Rule 3 of the brief: **nothing truncates the
 * venue.** That is the complaint this package answers — "Concert · 20:00 ·
 * Philharm…" was the venue paying for everything else on one line — and it is
 * why `venue` comes back as its own value rather than joined into a string the
 * component would have to ellipsise as a whole.
 *
 * The venue is the building, not the hall: "Philharmonie Berlin, Grosser Saal"
 * is two facts and the second is only worth room on a place page, where every
 * row shares the first — which is what `showHall` is.
 *
 * `more` is the *other* cinemas a film plays in, so a row says "Il Kino +3"
 * without naming three buildings it has no room for. Buildings, not halls —
 * `cinemaCount`'s own rule — and 0 on everything that is not a film, because
 * nothing else plays in several places at once.
 *
 * **Cards WP4: a set names itself.** Where the row's `places` covers two or
 * more buildings, `venue` is `setPlace`'s — the two names, or "10 cinemas" —
 * and `more` is 0, because the venue slot already counted them. That is the
 * same line extended, not a second one: a row with one place, or one cached
 * before `places` existed, prints exactly what it printed before.
 *
 * An exhibition trades `runCount` for its district, which is the brief's
 * table: a show is decided on where in the city it is, and the bar under the
 * row already answers how long is left.
 */
export function whereLine(
  e: RowEntry,
  ctx: { now: Date; showings?: Showing[]; showHall?: boolean },
): { venue: string; more: number; parts: string[]; placeId: string | null } {
  const kind = kindWord(e)
  const isExhibition = kind === 'Exhibition' || kind === 'Opening'
  const set = setPlace(e, ctx)
  const more = !set && kind === 'Film' ? Math.max(0, cinemaCount(ctx.showings) - 1) : 0
  const parts = isExhibition
    ? [e.district, priceText(e)]
    : [runCount(e, ctx), priceText(e)]
  return {
    venue: set ?? placeName(e, ctx.showHall),
    more,
    parts: parts.filter((x): x is string => !!x),
    // a1-28: the place page the venue name opens. None for a set ("Zoo
    // Palast, Rollberg Kinos" is no one place), none on a place page (it
    // would link to itself), none where no venue row exists (a mailed
    // place is text, `placeName`).
    placeId: set || ctx.showHall ? null : placeIdOf(e),
  }
}

/**
 * What it costs, or nothing.
 *
 * A range where both ends are known, because "€15–170" is the fact an opera
 * house publishes and "from €15" hides the half of it that decides whether
 * you go. `factOf`'s "from €15" stays as it is: it is the older one-line
 * row's, where the price shared its slot with the language and a rating and
 * one number was all that fitted.
 *
 * A zero that is not `is_free` is not a price at all — see
 * `placeholderPrice`, which `factOf` reads too so the card and the one-line
 * row cannot disagree.
 *
 * `price_note` is not read. It is the house's own German string — "Preise
 * variieren", "zzgl. Gebühren", and on 219 rows the raw schema.org fragment
 * the zero came from — and a row is not the place for it.
 */
function priceText(e: RowEntry): string | null {
  if (e.is_free) return 'Free'
  if (placeholderPrice(e)) return null
  const { price_min: lo, price_max: hi } = e
  const sym = priceSymbol(currencyOfCity(e.city))
  if (lo != null && hi != null && hi > lo) return `${sym}${lo}–${hi}`
  if (lo != null) return `${sym}${lo}`
  if (hi != null) return `${sym}${hi}`
  return null
}

/**
 * **How often**: "once", "last of 3", "51 showings to Wed", "4 dates to
 * 9 Oct", "opens 5 Oct", "3 weeks left".
 *
 * The prototype's `runCount`, with three things the app has that the prototype
 * did not:
 *
 * - **`isPermanent` returns null.** A countdown on a collection display that
 *   closes in 2029 is a countdown that never moves, and `whenText` already
 *   says "Permanent".
 * - **"last of 3".** The brief's concert line is "once / last of 3", which the
 *   prototype has no word for — it printed "once" for the last night of a run
 *   of three, which is true and says nothing about what you are about to miss.
 *   One future occurrence out of several that existed is the difference, and
 *   the showings are the only thing that knows both numbers.
 * - **`showing_count` where the caller passed no showings.** Most screens do
 *   not fetch them; the column is the entry's own performance count (openings
 *   and run markers excluded, `showingFacts` in `src/harvest/store.ts`) and it
 *   is not filtered to the future, so a row without showings can say how many
 *   there are and not which is last.
 *
 * The noun follows the kind, which is the brief's table: a cinema has
 * showings and a house has dates.
 */
export function runCount(e: RowEntry, ctx: { now: Date; showings?: Showing[] }): string | null {
  const today = berlinDay(ctx.now)
  if (isPermanent(e, ctx.now)) return null

  // Occurrences before the span, which is the prototype's order read onto the
  // app's data rather than its shape. There a "run" was an exhibition with no
  // individual dates; here every row that *has* dates also carries
  // `run_start`/`run_end` as their outer edges — a cinema's week, a
  // production's season — so taking the span first answered "1 week left" for
  // a film with fourteen screenings and "8 weeks left" for a production with
  // four dates. What you are deciding on is the dates.
  // One night once (cards WP4): `showing_list` resolves a twin's showings to
  // its canonical, so a night two sources list arrives twice — The Odyssey
  // read "42 showings" for 37. And with no showings to hand, a film counts
  // its set's future showings (`aheadCount`), not `showing_count`, which
  // since cards WP1 keeps the film's past weeks too.
  const occurrences = uniqueShowings((ctx.showings ?? []).filter(s => s.kind !== 'run'))
  const counted = !!ctx.showings
  const total = counted ? occurrences.length : aheadCount(e)
  if (total) {
    const from = ctx.now.toISOString()
    const future = counted ? occurrences.filter(s => s.starts_at >= from) : []
    const ahead = counted ? future.length : total
    if (!ahead) return null
    if (ahead === 1) return total > 1 ? `last of ${total}` : 'once'

    const noun = kindWord(e) === 'Film' ? 'showings' : 'dates'
    // `showing_count` is not filtered to the future, so a row with no
    // showings fetched can say how many there are and not which is last.
    if (!counted) return `${ahead} ${noun}`
    const last = rowDay(e, future.reduce((a, b) => (a.starts_at > b.starts_at ? a : b)).starts_at)
    return last === today ? `${ahead} ${noun}` : `${ahead} ${noun} to ${dateText(last, ctx.now)}`
  }

  if (e.run_start && e.run_end && e.run_end !== e.run_start) {
    if (e.run_start > today) return `opens ${dateText(e.run_start, ctx.now)}`
    const left = daysBetween(today, e.run_end)
    if (left < 0) return null
    if (left < 7) return `${left} ${left === 1 ? 'day' : 'days'} left`
    const weeks = Math.round(left / 7)
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} left`
  }

  // No showing rows at all and no span, but a date: a one-evening listing
  // whose source gave a day and never a performance row. 26 tours and every
  // talk.
  return (e.run_start ?? e.published_on) ? 'once' : null
}

/** Whole days from one calendar day to another. `D` pins both to noon UTC, so
 *  a clock change inside the span cannot make this 6.96 and floor to 6. */
const daysBetween = (from: string, to: string) =>
  Math.round((D(to).getTime() - D(from).getTime()) / 86400000)

// ---------------------------------------------------------------------------
// The exhibition bar
// ---------------------------------------------------------------------------

/**
 * How much of a run is left, for the bar under an exhibition row.
 *
 * Carried here from `cardShape.ts`, which this package deletes: the bar is the
 * one thing of the nine shapes' furniture worth keeping, and this is the
 * dependency-free file the manager's note asks for. Returns null where there
 * is nothing to be urgent about — no end date, or an end so far off that the
 * bar would be a flat line.
 */
export function runProgress(e: RowEntry, today: string): { pct: number; weeksLeft: number } | null {
  if (!e.run_start || !e.run_end) return null
  const start = Date.parse(e.run_start)
  const end = Date.parse(e.run_end)
  const now = Date.parse(today)
  if (!(end > start)) return null

  const weeksLeft = Math.max(0, Math.round((end - now) / (7 * 86400000)))
  const pct = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100))
  return { pct, weeksLeft }
}

// ---------------------------------------------------------------------------
// Sharing
// ---------------------------------------------------------------------------

/**
 * What E3's share sheet hands the operating system.
 *
 * The fields and nothing else: what it is, when, where, and the link. A share
 * is the one place a sentence would be tempting — somebody else is going to
 * read this without the app around it — and it is still not the app's to write
 * (B1). `text` is the three facts the row already states, in the order the row
 * states them.
 */
export function sharePayload(e: RowEntry, now: Date, url?: string | null): {
  title: string
  text: string
  url?: string
} {
  const where = e.building ?? e.venue ?? e.organisation ?? null
  return {
    title: rowTitle(e),
    text: [rowTitle(e), whenText(e, now), where].filter(Boolean).join(' · '),
    ...(url ? { url } : {}),
  }
}
